'use client';

import {
	ENUM_UTILS,
	UIMarket,
	UISerializableInsuranceFundStakeRecord,
} from '@drift/common';
import { useEffect, useMemo, useState } from 'react';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import TableV2 from '../Tables/TableV2';
import { StakeAction } from '@drift-labs/sdk';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import { Info, Open, Swap } from '@drift-labs/icons';
import { sortRecordsByTs } from 'src/utils/events';
import { getUrlForTx } from 'src/utils/explorer';
import { HistoryTableUIProps } from 'src/hooks/historyData/historyDataTypes';
import { useIfStakingHistory } from 'src/hooks/historyData/useIfStakingHistory';
import useHistoryTableFilterStore from 'src/stores/historyTableFilterStore/useHistoryTableFilterStore';
import { useSpotMarketsSortedByMarketIndex } from 'src/hooks/spotMarkets/useSpotMarketsSortedByMarketIndex';
import ToggleHeaderCell from 'src/components/Tables/cells/ToggleHeaderCell';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import Link from 'src/components/primitives/Link/Link';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';
import MarketIcon from 'src/components/Utils/MarketIcon';
import IconButton from 'src/components/primitives/IconButton/IconButton';
import Button from 'src/components/primitives/Button/Button';
import Env from 'src/environmentVariables/EnvironmentVariables';
import HistoryTable from 'src/components/HistoryTables/HistoryTable';

const PAGE_SIZE = 20;
const EMPTY_IF_STAKE_ARRAY: UISerializableInsuranceFundStakeRecord[] = [];
const HEADERS = ['Time', 'Vault', 'Action', 'Amount', ''];
const TABLE_GRID = '1fr 1fr 1fr 1fr 50px';

const InfoRow = ({ label, value }: { label: string; value: string }) => {
	return (
		<div className="flex w-full justify-between">
			<div>{label}</div>
			<div>{value}</div>
		</div>
	);
};

const V2IFStakeHistoryTableDisplay = (
	props: HistoryTableUIProps<UISerializableInsuranceFundStakeRecord>
) => {
	const spotMarketsSortedByMarketIndex = useSpotMarketsSortedByMarketIndex();
	const filters = useHistoryTableFilterStore((s) =>
		s.getFilterState('if-staking')
	);
	const currentPage = filters.currentUiPage;

	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	const devSwitchIsOn = useDevSwitchIsOn();

	const isMobileScreenSize = useIsMobileScreenSize();

	// Use history data from hook instead of Zustand store
	const { historyData } = props;

	const ifStakeHistory: UISerializableInsuranceFundStakeRecord[] =
		historyData.initialHistoryLoaded
			? historyData.records
			: EMPTY_IF_STAKE_ARRAY;

	const filteredIfStakingHistory = useMemo(
		() =>
			props.filterFn
				? ifStakeHistory?.filter((record) => props.filterFn(record, filters))
				: ifStakeHistory,
		[ifStakeHistory, props.filterFn, filters]
	);

	const maxNumOfRecordsToDisplay =
		filters.getFilterMode() === 'SHORT_TERM'
			? (historyData.totalCount ?? 0) // short term data API returns pages of 20 records up to the current page, and we do a workaround to (+1 to total count) when we know there is a next page
			: (filteredIfStakingHistory?.length ?? 0); // long term data API returns up to 5000 records for the first page, so we check the actual displayed records (filtered records) length

	const [currentIfStakeHistory, setCurrentIfStakeHistory] = useState(
		ifStakeHistory?.slice(0, PAGE_SIZE) ?? undefined // undefined state needed for loading state in TableStateWrapper
	);

	useEffect(() => {
		setCurrentIfStakeHistory(
			filteredIfStakingHistory?.slice(0, PAGE_SIZE) ?? undefined
		);
	}, [filteredIfStakingHistory]);

	const getStakeActionString = (ifStakeAction: StakeAction) => {
		if (ENUM_UTILS.match(ifStakeAction, StakeAction.STAKE)) {
			return 'Stake';
		} else if (ENUM_UTILS.match(ifStakeAction, StakeAction.UNSTAKE_REQUEST)) {
			return 'Request Unstake';
		} else if (
			ENUM_UTILS.match(ifStakeAction, StakeAction.UNSTAKE_CANCEL_REQUEST)
		) {
			return 'Cancel Unstake Request';
		} else if (ENUM_UTILS.match(ifStakeAction, StakeAction.UNSTAKE)) {
			return 'Unstake';
		} else if (ENUM_UTILS.match(ifStakeAction, StakeAction.STAKE_TRANSFER)) {
			return `Claimed Drift Draw Prize`;
		} else {
			return '';
		}
	};

	return (
		<HistoryTable
			tableGrid={TABLE_GRID}
			pageSize={PAGE_SIZE}
			totalItems={maxNumOfRecordsToDisplay}
			currentPage={currentPage}
			pageChangeCallback={props.executePageChange}
			loading={historyData.loading ?? false}
			tableState={{
				records: currentIfStakeHistory,
				id: 'if_stake_history_table',
			}}
			tableContentTop={
				<TableV2.HeaderRow grid={TABLE_GRID}>
					{HEADERS.map((header) => (
						<TableV2.HeaderCell key={header}>
							{header.toLowerCase() === 'time' ? (
								<ToggleHeaderCell
									label={header}
									onClick={toggleDateDisplayType}
								/>
							) : (
								header
							)}
						</TableV2.HeaderCell>
					))}
				</TableV2.HeaderRow>
			}
			tableContentMiddle={sortRecordsByTs(currentIfStakeHistory).map(
				(ifStakeRecord) => {
					const market =
						spotMarketsSortedByMarketIndex[ifStakeRecord.marketIndex];

					const ifStakeAmount = ifStakeRecord.amount
						.toNum()
						.toLocaleString(Env.locale);

					if (isMobileScreenSize) {
						return (
							<MobileTableCard.Root
								key={ifStakeRecord.ts.toString()}
								className="gap-y-lg"
							>
								<div className="flex items-center gap-x-xs">
									<MarketIcon
										marketSymbol={
											UIMarket.createSpotMarket(ifStakeRecord.marketIndex)
												.uiSymbols.baseAssetSymbol
										}
										sizeClass="size-5"
									/>
									<span className="typo-t2">
										{
											UIMarket.createSpotMarket(ifStakeRecord.marketIndex)
												.uiSymbols.baseAssetSymbol
										}
									</span>
								</div>

								<div className="grid w-full grid-cols-2">
									<MobileTableCard.LabelWithValue
										label="Amount"
										value={`${ifStakeAmount} ${market.symbol}`}
									/>
									<MobileTableCard.LabelWithValue
										label="Action"
										className="items-end"
										value={getStakeActionString(ifStakeRecord.action)}
									/>
								</div>

								<div className="flex items-center gap-x-xs">
									<MobileTableCard.Label>Time</MobileTableCard.Label>
									<IconButton
										size="sm"
										variant="tertiary"
										className="-m-md p-md"
										onClick={toggleDateDisplayType}
										icon={Swap}
									/>
									<TableV2.DateCell
										className="w-fit py-0 whitespace-nowrap"
										displayAsTimestamp={displayDatesAsTimestamp}
										date={new Date(ifStakeRecord.ts.toNumber() * 1000)}
									/>
								</div>

								<Button
									size="sm"
									variant="secondary"
									as="a"
									href={getUrlForTx(ifStakeRecord.txSig)}
									openInNewTab
								>
									View in scanner
								</Button>
							</MobileTableCard.Root>
						);
					}

					return (
						<TableV2.BodyRow
							key={ifStakeRecord.ts.toString()}
							className="h-10 border-0"
							grid={TABLE_GRID}
							isDataRow
						>
							<TableV2.DateCell
								date={new Date(ifStakeRecord.ts.toNumber() * 1000)}
								displayAsTimestamp={displayDatesAsTimestamp}
							/>

							<TableV2.MarketCell marketSymbol={market.symbol} />

							<TableV2.BodyCell>
								{getStakeActionString(ifStakeRecord.action)}
							</TableV2.BodyCell>

							<TableV2.BodyCell>
								{ifStakeAmount} {market.symbol}
							</TableV2.BodyCell>

							<TableV2.BodyCell className="justify-center">
								<Link
									variant="secondary"
									href={getUrlForTx(ifStakeRecord.txSig)}
									rightIcon="none"
									openInNewTab
								>
									<Open size={16} />
								</Link>
							</TableV2.BodyCell>

							{devSwitchIsOn && (
								<Tooltip
									className="absolute right-10 translate-y-1/2 text-text-secondary"
									content={
										<>
											<InfoRow label="Vault:" value={market.symbol} />
											<InfoRow
												label="Insurance vault amount before:"
												value={ifStakeRecord.insuranceVaultAmountBefore
													.toNum()
													.toString()}
											/>
											<InfoRow
												label="IF shares before:"
												value={ifStakeRecord.ifSharesBefore.toNum().toString()}
											/>
											<InfoRow
												label="IF shares after:"
												value={ifStakeRecord.ifSharesAfter.toNum().toString()}
											/>
											<InfoRow
												label="User IF shares before:"
												value={ifStakeRecord.userIfSharesBefore
													.toNum()
													.toString()}
											/>
											<InfoRow
												label="User IF shares after:"
												value={ifStakeRecord.userIfSharesAfter
													.toNum()
													.toString()}
											/>
											<InfoRow
												label="Total IF shares before:"
												value={ifStakeRecord.totalIfSharesBefore
													.toNum()
													.toString()}
											/>
											<InfoRow
												label="Total IF shares after:"
												value={ifStakeRecord.totalIfSharesAfter
													.toNum()
													.toString()}
											/>
										</>
									}
								>
									<Info />
								</Tooltip>
							)}
						</TableV2.BodyRow>
					);
				}
			)}
		/>
	);
};

const V2IFStakeHistoryTable = () => {
	const tableState = useIfStakingHistory();

	return <V2IFStakeHistoryTableDisplay {...tableState} />;
};

export default V2IFStakeHistoryTable;
